import React, { FC, useEffect } from 'react';
import { Card, HighlightedCard } from '@edreamsodigeo/prisma-design-system';
import useAirlineSteering from 'clientState/hooks/useAirlineSteering';
import { usePrime } from 'clientState/providers/PrimeProvider';
import FlexibleDatesRepricingMessageDecorator, {
  FlexibleDatesRepricingMessagePositionType,
} from '@components/Results/Components/FlexibleDates/FlexibleDatesRepricingMessage/FlexibleDatesRepricingMessageDecorator';
import AirlineCampaignHeaderRevamp from '../AirlineCampaignHeader';
import HighlightedDecorator from '@components/Results/Components/ThreeColumnsItinerary/HighlightedDecorator/HighlightedDecorator';
import { useFlightOfTheDayItineraryIds } from 'clientState/providers/SearchResponseProvider';
import { HighlightedType } from '@components/Results/Components/ItineraryHeader/constants/highlightedTypes';
import { useFlightOfTheDayTracking } from '@components/Results/Components/Itinerary/useFlightOfTheDayTracking';
import { ItineraryDecorationProps } from '../../../types';
import { useEqualProminencePrimeDisplayMobileInResults } from 'clientState/hooks/useEqualProminencePrimeDisplayMobileInResults';

const AirlineCampaignDecorationMobile: FC<ItineraryDecorationProps> = ({
  itinerary,
  position,
  highlightedType = 'default',
  hasAirlineCampaign,
  isMeta,
  hasRetailingTechniques,
  isAlternativeItinerary,
  hasAlternativeItineraryDecorator,
  isUnavailable = false,
  isAlternativeItineraryOnModal,
  children,
  ...props
}) => {
  const isEqualProminencePrimeDisplayMobileInResults =
    useEqualProminencePrimeDisplayMobileInResults();
  const { trackFlightOfTheDay } = useFlightOfTheDayTracking('show');
  const flightOfTheDayItineraryIds = useFlightOfTheDayItineraryIds();
  const id = itinerary?.id || '0';
  const isFlightOfTheDay =
    (flightOfTheDayItineraryIds.includes(id) &&
      highlightedType !== HighlightedType.Recommended &&
      highlightedType !== HighlightedType.Personalised) ||
    highlightedType === HighlightedType.FlightOfTheDay;

  useEffect(() => {
    if (isFlightOfTheDay) {
      trackFlightOfTheDay(itinerary, position, hasRetailingTechniques);
    }
  }, [
    isFlightOfTheDay,
    hasRetailingTechniques,
    itinerary,
    position,
    trackFlightOfTheDay,
  ]);

  const { isUK, isPrimeUser, isPrimeSite } = usePrime() || {};
  const withUKStyle = isUK && !isPrimeUser && isPrimeSite;

  const hasDecoration =
    (highlightedType === 'recommended' || hasAirlineCampaign) &&
    hasRetailingTechniques &&
    !isAlternativeItinerary;
  const Component = hasDecoration ? HighlightedCard : Card;
  let CardComponent = Component;

  if (isFlightOfTheDay && hasRetailingTechniques) {
    highlightedType = HighlightedType.FlightOfTheDay;
    CardComponent = Card;
  }

  const type = hasDecoration
    ? hasAirlineCampaign
      ? 'promotional'
      : 'positive'
    : null;

  const sx =
    (isAlternativeItinerary && !hasAlternativeItineraryDecorator) ||
    isUnavailable
      ? {
          backgroundColor: 'neutral.20',
          border: '1px solid',
          borderColor: 'neutral.60',
        }
      : {};

  const sxOnModal =
    isAlternativeItineraryOnModal && !isAlternativeItinerary
      ? { border: '1px solid', borderColor: 'neutral.60', boxShadow: 'none' }
      : { boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.1)' };

  const definePadding = () => {
    if (isEqualProminencePrimeDisplayMobileInResults) {
      return '12px 2px 2px 2px';
    }
    return withUKStyle ? '0' : 'base';
  };

  return (
    <>
      {hasAirlineCampaign &&
        !isMeta &&
        highlightedType !== 'recommended' &&
        !isAlternativeItinerary && <AirlineCampaignHeaderRevamp />}
      <FlexibleDatesRepricingMessageDecorator
        position={FlexibleDatesRepricingMessagePositionType.belowCard}
      >
        <HighlightedDecorator
          CardComponent={CardComponent}
          type={type}
          highlightedType={highlightedType}
          {...props}
          sx={{ ...sx, ...sxOnModal }}
          p={definePadding()}
        >
          {children}
        </HighlightedDecorator>
      </FlexibleDatesRepricingMessageDecorator>
    </>
  );
};

const AirlineCampaignDecorationRevamp: FC<ItineraryDecorationProps> = ({
  children,
  itinerary,
  position,
  highlightedType = 'default',
  isMeta,
  hasRetailingTechniques,
  isAlternativeItinerary,
  hasAlternativeItineraryDecorator,
  isUnavailable,
  isAlternativeItineraryOnModal,
  ...props
}) => {
  const { hasAirlineCampaignData } = useAirlineSteering();

  return (
    <AirlineCampaignDecorationMobile
      itinerary={itinerary}
      position={position}
      highlightedType={highlightedType}
      hasAirlineCampaign={hasAirlineCampaignData()}
      isMeta={isMeta}
      hasRetailingTechniques={hasRetailingTechniques}
      isAlternativeItinerary={isAlternativeItinerary}
      hasAlternativeItineraryDecorator={hasAlternativeItineraryDecorator}
      isUnavailable={isUnavailable}
      isAlternativeItineraryOnModal={isAlternativeItineraryOnModal}
      {...props}
    >
      {children}
    </AirlineCampaignDecorationMobile>
  );
};

export default AirlineCampaignDecorationRevamp;
